import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import dossierService from "./dossierService";

export const getDossiers = createAsyncThunk(
  "dossiers/getDossiers",
  async (params = {}, thunkAPI) => {
    try {
      return await dossierService.getDossiers(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const getDossierById = createAsyncThunk(
  "dossiers/getDossierById",
  async (id, thunkAPI) => {
    try {
      return await dossierService.getDossierById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const createDossier = createAsyncThunk(
  "dossiers/createDossier",
  async (payload, thunkAPI) => {
    try {
      return await dossierService.createDossier(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const updateDossier = createAsyncThunk(
  "dossiers/updateDossier",
  async ({ id, data }, thunkAPI) => {
    try {
      return await dossierService.updateDossier({
        id,
        data,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const updateDossierValues = createAsyncThunk(
  "dossiers/updateDossierValues",
  async ({ id, values }, thunkAPI) => {
    try {
      return await dossierService.updateDossierValues({
        id,
        values,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const archiveDossier = createAsyncThunk(
  "dossiers/archiveDossier",
  async (id, thunkAPI) => {
    try {
      return await dossierService.archiveDossier(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const restoreDossier = createAsyncThunk(
  "dossiers/restoreDossier",
  async (id, thunkAPI) => {
    try {
      return await dossierService.restoreDossier(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

const initialState = {
  dossiers: [],

  selectedDossier: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  },

  filters: {
    search: "",
    status: "",
    school_year: "",
  },

  isLoading: false,
  isCreating: false,
  isUpdating: false,

  error: null,
};

const updateDossierInList = (list, dossier) => {
  return list.map((item) => (item._id === dossier._id ? dossier : item));
};

const dossierSlice = createSlice({
  name: "dossiers",

  initialState,

  reducers: {
    clearDossierError: (state) => {
      state.error = null;
    },

    setSelectedDossier: (state, action) => {
      state.selectedDossier = action.payload;
    },

    setDossierFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    setDossierPage: (state, action) => {
      state.pagination.page = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getDossiers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getDossiers.fulfilled, (state, action) => {
        state.isLoading = false;

        state.dossiers =
          action.payload?.data?.items || action.payload?.data || [];

        state.pagination =
          action.payload?.pagination || initialState.pagination;
      })

      .addCase(getDossiers.rejected, (state, action) => {
        state.isLoading = false;

        state.error = action.payload;
      })

      .addCase(getDossierById.fulfilled, (state, action) => {
        state.selectedDossier = action.payload?.data || null;
      })

      .addCase(createDossier.pending, (state) => {
        state.isCreating = true;
      })

      .addCase(createDossier.fulfilled, (state, action) => {
        state.isCreating = false;

        if (action.payload?.data) {
          state.dossiers.unshift(action.payload.data);
        }
      })

      .addCase(createDossier.rejected, (state, action) => {
        state.isCreating = false;

        state.error = action.payload;
      })

      .addCase(updateDossier.pending, (state) => {
        state.isUpdating = true;
      })

      .addCase(updateDossier.fulfilled, (state, action) => {
        state.isUpdating = false;

        if (action.payload?.data) {
          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );

          state.selectedDossier = action.payload.data;
        }
      })

      .addCase(updateDossierValues.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.selectedDossier = action.payload.data;

          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );
        }
      })

      .addCase(archiveDossier.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );
        }
      })

      .addCase(restoreDossier.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );
        }
      });
  },
});

export const {
  clearDossierError,
  setSelectedDossier,
  setDossierFilters,
  setDossierPage,
} = dossierSlice.actions;

export default dossierSlice.reducer;
